/**
 * sessionStorage-backed flags used to distinguish auth state transitions.
 *
 * Two flags live here, both single-use within a browser session:
 *
 *   - `INTENTIONAL_SIGN_OUT_KEY` — set by `useAuth.signOut()` immediately
 *     before calling `client.auth.signOut()`. AuthProvider's
 *     `onAuthStateChange` listener consumes this when SIGNED_OUT fires
 *     — if present, the sign-out was user-initiated (avatar menu), no
 *     banner. If absent, SIGNED_OUT came from Supabase JS's internal
 *     token-refresh failure path → mark the session as expired.
 *
 *   - `SESSION_EXPIRED_KEY` — set by AuthProvider when an unexpected
 *     SIGNED_OUT is observed. LandingPage reads + clears it on mount
 *     and renders an amber banner explaining the bounce-out so the
 *     user understands why they're back at the marketing surface.
 *
 * **Why this is its own file**: `react-refresh/only-export-components`
 * blocks component files from exporting non-component values. AuthProvider
 * needs to both write these flags (inside the listener) and re-export the
 * read helpers to the LandingPage / useAuth.signOut callsites — so the
 * helpers live separately and the component module is HMR-friendly. Same
 * pattern as `dataBackupKeys.ts`.
 */

const INTENTIONAL_SIGN_OUT_KEY = 'debtos:intentionalSignOut:v1'
const SESSION_EXPIRED_KEY = 'debtos:sessionExpired:v1'

export function markIntentionalSignOut(): void {
  try {
    sessionStorage.setItem(INTENTIONAL_SIGN_OUT_KEY, '1')
  } catch {
    // Private browsing — fall through. The banner will show after an
    // intentional sign-out in that mode; mildly redundant but not wrong.
  }
}

export function consumeIntentionalSignOut(): boolean {
  try {
    const v = sessionStorage.getItem(INTENTIONAL_SIGN_OUT_KEY)
    if (v) sessionStorage.removeItem(INTENTIONAL_SIGN_OUT_KEY)
    return v === '1'
  } catch {
    return false
  }
}

export function markSessionExpired(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1')
  } catch {
    // Storage unavailable — the banner won't show, but the router still
    // sends the user to the landing where they can sign in again.
  }
}

export function consumeSessionExpired(): boolean {
  try {
    const v = sessionStorage.getItem(SESSION_EXPIRED_KEY)
    if (v) sessionStorage.removeItem(SESSION_EXPIRED_KEY)
    return v === '1'
  } catch {
    return false
  }
}
